import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { ACTIVATE_STAFF, DEACTIVATE_STAFF, DEL_STAFF } from "apollo/mutuations";
import { GET_STAFFS } from "apollo/queries";
import SalesDetailModal from "components/Sales/SalesDetailModal";
import { SalesAddModal } from "components/Staff/AddStaff";
import { StaffUpdateModal } from "components/Staff/UpdateStaff";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { Trash } from "tabler-icons-react";

const Staffs = () => {
  const [openedStatusChange, setStatusChange] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null); // 'ACTIVE' or 'SUSPENDED'
  const [size, setSize] = useState("10");
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [opened, setOpened] = useState(false);
  const [editId, setEditId] = useState();
  const [selectedID, setSelectedID] = useState(null);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);

  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // --- Apollo Hooks ---
  const { data, loading, fetchMore, refetch } = useQuery(GET_STAFFS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
    },
  });

  const [activateStaff] = useMutation(ACTIVATE_STAFF, {
    onCompleted: () => {
      showNotification({
        color: "green",
        title: "Success",
        message: "Staff Activated",
      });
      setStatusChange(false);
      refetch();
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.message,
      });
    },
  });

  const [deactivateStaff] = useMutation(DEACTIVATE_STAFF, {
    onCompleted: () => {
      showNotification({
        color: "green",
        title: "Success",
        message: "Staff Deactivated",
      });
      setStatusChange(false);
      refetch();
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.message,
      });
    },
  });

  const [delStaff] = useMutation(DEL_STAFF, {
    onCompleted: () => {
      setOpenedDelete(false);
      refetch();
      showNotification({
        color: "green",
        title: "Success",
        message: "Staff Deleted",
      });
    },
  });

  const handleStatusModal = (id, status) => {
    setSelectedID(id);
    setTargetStatus(status);
    setStatusChange(true);
  };

  const executeStatusChange = () => {
    if (targetStatus === "ACTIVE") {
      activateStaff({ variables: { id: selectedID } });
    } else {
      deactivateStaff({ variables: { id: selectedID } });
    }
  };

  const handleDelete = (id) => {
    setSelectedID(id);
    setOpenedDelete(true);
  };

  const handleEditSales = (rowData) => {
    setEditId(rowData);
    setOpenedEdit(true);
  };

  const handleManageSales = (rowData) => {
    setEditId(rowData);
    setOpenedDetail(true);
  };

  const handleManualSearch = (searchTerm) => setSearchValue(searchTerm);
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  const handleChange = (currentPage) => {
    fetchMore({ variables: { first: parseInt(size), page: currentPage } });
    setActivePage(currentPage);
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);
  useEffect(() => {
    if (data) setTotal(data.staffs.paginatorInfo.lastPage);
  }, [data, size]);

  if (!hasMounted) return null;

  const headerData = [
    {
      label: "Name",
      key: "name",
      render: (rowData) => <span>{rowData?.name}</span>,
    },
    {
      label: "Phone",
      key: "phone",
      render: (rowData) => <span>{rowData?.phone}</span>,
    },
    {
      label: "Email",
      key: "email",
      render: (rowData) => <span>{rowData?.email}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      width: "28%",
      render: (rowData) => {
        const isActive = rowData.status?.toLowerCase() === "active";
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton label="Edit" onClick={() => handleEditSales(rowData)}>
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(rowData.id)}
            >
              <Trash size={15} />
            </ActionButton>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleManageSales(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
            <ActionButton
              label={isActive ? "Deactivate" : "Activate"}
              color={isActive ? "deactivate" : "activate"}
              onClick={() =>
                handleStatusModal(
                  rowData.id,
                  isActive ? "DEACTIVATED" : "ACTIVE",
                )
              }
            >
              {isActive ? (
                <PersonOffIcon size={15} />
              ) : (
                <HowToRegIcon size={15} />
              )}
            </ActionButton>
          </div>
        );
      },
    },
  ];

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        onClose={() => setOpenedEdit(false)}
        title="Editing Staff"
        padding="xl"
        position="bottom"
        size="80%"
      >
        <StaffUpdateModal
          activePage={activePage}
          editRow={editId}
          setOpened={setOpenedEdit}
        />
      </Drawer>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Adding Staff"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <SalesAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          refetchBusinesses={refetch}
          size={size}
          setActivePage={setActivePage}
          setOpened={setOpened}
        />
      </Drawer>

      <Modal
        opened={openedStatusChange}
        onClose={() => setStatusChange(false)}
        title="Confirm Action"
        centered
      >
        <p>{`Are you sure you want to ${
          targetStatus === "ACTIVE" ? "Activate" : "Deactivate"
        } this user?`}</p>
        <Group position="right">
          <Button
            onClick={executeStatusChange}
            color={targetStatus === "ACTIVE" ? "green" : "red"}
          >
            {targetStatus === "ACTIVE" ? "Activate" : "Deactivate"}
          </Button>
        </Group>
      </Modal>

      <Drawer
        opened={openedDetail}
        onClose={() => setOpenedDetail(false)}
        title="Staff Detail"
        padding="xl"
        position="bottom"
        size="80%"
      >
        <SalesDetailModal sales={editId} />
      </Drawer>

      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure you want to delete this staff?</p>
        <Group position="right">
          <Button
            onClick={() => delStaff({ variables: { id: selectedID } })}
            color="red"
          >
            Delete
          </Button>
        </Group>
      </Modal>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            paginatorInfo={data?.staffs?.paginatorInfo}
            activePage={activePage}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            optionsData={{ actionLabel: "Add Staff", setAddModal: setOpened }}
            loading={loading}
            data={data ? data.staffs.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default Staffs;
